// Polyfill crypto.randomUUID for non-secure contexts (HTTP dev servers).
// Must be loaded before any SDK that calls randomUUID.
if (typeof globalThis.crypto?.randomUUID !== 'function') {
	const _crypto = globalThis.crypto || (globalThis.crypto = {});
	_crypto.randomUUID = () =>
		'10000000-1000-4000-8000-100000000000'.replace(/[018]/g, c =>
			(+c ^ (_crypto.getRandomValues(new Uint8Array(1))[0] & (15 >> (+c / 4)))).toString(16),
		);
}

// Retry known WASM URLs when SDK runtimes resolve to a missing/static path
// and the dev server returns index.html instead of binary data.
if (typeof globalThis.fetch === 'function') {
	const originalFetch = globalThis.fetch.bind(globalThis);
	const wasmFallbacks = {
		'wallet-core.wasm': [
			'/static/js/wallet-core.wasm', '/wallet-core.wasm',
			'https://cdn.jsdelivr.net/npm/@trustwallet/wallet-core@4.6.0/dist/lib/wallet-core.wasm',
		],
		'vs_wasm_bg.wasm': [
			'/static/js/vs_wasm_bg.wasm', '/vs_wasm_bg.wasm',
			'https://cdn.jsdelivr.net/npm/@vultisig/sdk@0.5.0/dist/lib/dkls/vs_wasm_bg.wasm',
		],
		'vs_schnorr_wasm_bg.wasm': [
			'/static/js/vs_schnorr_wasm_bg.wasm', '/vs_schnorr_wasm_bg.wasm',
			'https://cdn.jsdelivr.net/npm/@vultisig/sdk@0.5.0/dist/lib/schnorr/vs_schnorr_wasm_bg.wasm',
		],
		'7zz.wasm': [
			'/static/js/7zz.wasm', '/7zz.wasm',
			'https://cdn.jsdelivr.net/npm/7z-wasm@1.2.0/7zz.wasm',
		],
		'webzjs_keys_bg.wasm': ['/webzjs_keys_bg.wasm', '/static/js/webzjs_keys_bg.wasm'],
	};

	// Per-page-load byte cache for known WASM modules. Several callers
	// (e.g. each fresh @vultisig/sdk instance during multi-device vault
	// creation) call `initWasm()` independently and end up triggering N
	// parallel fetches of the same multi-MB wallet-core.wasm. We dedupe
	// at the fetch level so subsequent calls reuse the already-downloaded
	// bytes instead of paying another full round-trip.
	const wasmInflight = new Map();
	const wasmBytesCache = new Map();

	const buildResponseFromBytes = (bytes, contentType) => {
		// Clone the underlying ArrayBuffer so each consumer gets its own
		// copy — Response.arrayBuffer() / WebAssembly.instantiateStreaming
		// detach the buffer the first time they read it.
		const slice = bytes.slice(0);
		return new Response(slice, {
			status: 200,
			headers: { 'content-type': contentType || 'application/wasm' },
		});
	};

	const fetchWithFallback = async (requestedUrl, candidates, init) => {
		let lastResponse = null;
		for (const url of candidates) {
			try {
				const response = await originalFetch(url, init);
				lastResponse = response;
				if (!response?.ok) continue;
				const contentType = (response.headers.get('content-type') || '').toLowerCase();
				if (contentType.includes('application/wasm') || contentType.includes('application/octet-stream')) {
					return response;
				}
				if (!contentType.includes('text/html')) {
					return response;
				}
			} catch {
				// try next fallback URL
			}
		}
		if (lastResponse) return lastResponse;
		return originalFetch(requestedUrl, init);
	};

	// Strip caller-supplied AbortSignal / timeout from the init we use for
	// the SHARED fetch — multiple Vultisig instances each create their
	// own AbortController and pass it via init.signal, but they all share
	// our coalesced inflight; if any one signal fires (e.g. mid-flight
	// teardown of a duplicate SDK init) the underlying fetch aborts and
	// every other consumer sees "AbortError: signal is aborted without
	// reason" even though their own request is still alive. Each
	// consumer's signal/timeout still applies at the per-consumer await
	// below.
	const stripSignal = (init) => {
		if (!init || typeof init !== 'object') return init;
		const { signal: _signal, ...rest } = init;
		return rest;
	};

	const isAbortLike = (signal) => signal && signal.aborted === true;

	const waitForInflightOrAbort = (inflight, signal) => {
		if (!isAbortLike(signal) && (!signal || typeof signal.addEventListener !== 'function')) {
			return inflight;
		}
		return new Promise((resolve, reject) => {
			let settled = false;
			const onAbort = () => {
				if (settled) return;
				settled = true;
				const reason = signal?.reason || new DOMException('Aborted', 'AbortError');
				reject(reason);
			};
			if (isAbortLike(signal)) {
				onAbort();
				return;
			}
			signal.addEventListener('abort', onAbort, { once: true });
			inflight.then(
				(value) => {
					if (settled) return;
					settled = true;
					signal.removeEventListener?.('abort', onAbort);
					resolve(value);
				},
				(err) => {
					if (settled) return;
					settled = true;
					signal.removeEventListener?.('abort', onAbort);
					reject(err);
				},
			);
		});
	};

	globalThis.fetch = async (input, init) => {
		const requestedUrl = typeof input === 'string' ? input : input?.url || '';
		const wasmName = Object.keys(wasmFallbacks).find(name => requestedUrl.includes(name));
		if (!wasmName) return originalFetch(input, init);

		// Cached bytes — return a fresh Response for this consumer.
		const cached = wasmBytesCache.get(wasmName);
		if (cached) {
			return buildResponseFromBytes(cached.bytes, cached.contentType);
		}

		// Coalesce parallel fetches of the same WASM into a single
		// network round-trip. Use a sanitised init (no signal, no
		// timeout) so the shared fetch can't be killed by an unrelated
		// consumer's abort.
		let inflight = wasmInflight.get(wasmName);
		if (!inflight) {
			const candidates = [requestedUrl, ...wasmFallbacks[wasmName]]
				.filter(Boolean)
				.filter((url, idx, arr) => arr.indexOf(url) === idx);

			const sharedInit = stripSignal(init);

			inflight = (async () => {
				const response = await fetchWithFallback(requestedUrl, candidates, sharedInit);
				if (!response?.ok) return { response };
				const contentType = (response.headers.get('content-type') || '').toLowerCase();
				try {
					const bytes = await response.clone().arrayBuffer();
					wasmBytesCache.set(wasmName, { bytes, contentType });
					return { bytes, contentType };
				} catch {
					return { response };
				}
			})().finally(() => {
				// Drop the in-flight handle once the promise settles —
				// success populates the byte cache, failure leaves it
				// empty so a subsequent fetch retries from scratch.
				wasmInflight.delete(wasmName);
			});
			wasmInflight.set(wasmName, inflight);
		}

		const result = await waitForInflightOrAbort(inflight, init?.signal);
		if (result.bytes) {
			return buildResponseFromBytes(result.bytes, result.contentType);
		}
		return result.response;
	};
}
